import type { ReactNode } from "react";
import { createPath, useLocation, useMatches } from "react-router";
import { useTranslation } from "react-i18next";
import { documentNamespaces, hasRequiredTranslations } from "~/i18n";
import { OFFLINE_DOCUMENT_NAMESPACES } from "~/lib/pwa/offline-fallback";
import { reloadForTranslations } from "~/lib/translation-reload";

// Suspends until the document unloads: the previous screen stays up while the reload is under way.
const RELOADING = new Promise<never>(() => {});

function ReadyTranslations({ namespaces, required, href, children }: {
  namespaces: string[];
  required: string[];
  href: string;
  children: ReactNode;
}) {
  const { i18n } = useTranslation(namespaces, { useSuspense: true });
  // i18next marks failed reads as loaded too, and never requests them again in this document.
  // So the page is loaded again from the server, once (design B, founder 2026-11-02); a second
  // failure reaches the translated route error screen, never the destination's raw keys.
  if (!hasRequiredTranslations(i18n, required)) {
    if (typeof document !== "undefined" && reloadForTranslations(href)) throw RELOADING;
    throw new Error("Unable to load route translations");
  }
  return children;
}

/**
 * A navigation must not commit raw translation keys while its new dictionaries load.
 * The stable Suspense boundary in root keeps the previous screen during router transitions;
 * no clientLoader is needed (that would split single-fetch into extra Worker requests).
 */
export function RouteTranslations({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { pathname } = location;
  const matched = useMatches().flatMap(({ handle }) => {
    const namespaces = (handle as { i18n?: unknown } | undefined)?.i18n;
    if (typeof namespaces === "string") return [namespaces];
    return Array.isArray(namespaces) ? namespaces.filter((ns): ns is string => typeof ns === "string") : [];
  });
  // SSR already loaded matched namespaces. Private client navigations retain the complete
  // catalogue guarantee, while public destinations only need their routes and chrome.
  const client = typeof document !== "undefined";
  const namespaces = client ? documentNamespaces(pathname, matched) : matched;
  const required = client ? [...new Set([...OFFLINE_DOCUMENT_NAMESPACES, ...matched])] : matched;
  // react-i18next uses namespaces as a dependency array. Remount only this gate when the
  // set changes so React never receives a dependency array with a different length.
  return <ReadyTranslations key={namespaces.join(",")} namespaces={namespaces} required={required} href={createPath(location)}>
    {children}
  </ReadyTranslations>;
}
